/**
 * TodoLinkNodeView - メモに埋め込んだ Todo の表示。
 *
 * - 丸いチェックで完了 ↔ 未完了を切り替える (Todo 本体の status を更新する)
 * - 状態・締切・予定・プロジェクトをチップで出し、↗ で Todo ページの詳細を開く
 * - todoId が無い間はカレンダーと同じ追加ダイアログで新規作成 / 既存 Todo の選択を行う
 */

import { Selection } from '@tiptap/pm/state'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { format, isToday } from 'date-fns'
import {
  ArrowUpRight,
  Calendar,
  Circle,
  CircleCheck,
  CircleDot,
  CircleMinus,
  Clock,
  Folder,
  Loader2,
  Plus,
  RotateCw,
  X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import type { TodoResponse, TodoStatus } from '@/api/types/todo'
import { apiErrorMessage } from '@/lib/api-error'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'

import { createLinkedTodo, requestLinkedTodo, toggleLinkedTodo, useLinkedTodo } from './linked-todo-cache'
import { TodoInsertDialog } from './todo-insert-dialog'

// 挿入直後のノードだけダイアログを開く (メモを開いたときに残っていた空ノードでは開かない)
let dialogRequested = false
export const requestTodoInsertDialog = () => {
  dialogRequested = true
}

const formatDateTime = (iso: string): string => {
  const d = parseUtcDate(iso)
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0
  return format(d, hasTime ? 'M/d HH:mm' : 'M/d')
}

const chipClass = 'inline-flex shrink-0 items-center gap-0.5 rounded border px-1 py-px text-[11px] leading-4'

export const TodoLinkNodeView = ({ node, editor, getPos, selected }: NodeViewProps) => {
  const todoId = (node.attrs.todoId ?? null) as number | null
  const snapshotTitle = String(node.attrs.title ?? '')
  const entry = useLinkedTodo(todoId)
  const canEdit = editor.isEditable
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (todoId !== null || !dialogRequested) return
    dialogRequested = false
    setDialogOpen(true)
  }, [todoId])

  const todo = typeof entry === 'object' ? entry : null
  const fetchedTitle = todo?.title

  // 表示名のスナップショットを Todo 側の変更に合わせる (undo 履歴には積まない)
  useEffect(() => {
    if (!canEdit || fetchedTitle === undefined || fetchedTitle === snapshotTitle) return
    const pos = getPos()
    if (typeof pos !== 'number') return
    const tr = editor.state.tr.setNodeAttribute(pos, 'title', fetchedTitle).setMeta('addToHistory', false)
    editor.view.dispatch(tr)
  }, [canEdit, editor, fetchedTitle, getPos, snapshotTitle])

  // 未設定 (todoId なし) の自ノード位置。非同期処理の後でも位置がずれないよう毎回引き直す
  const emptyPos = (): number | null => {
    const pos = getPos()
    if (typeof pos !== 'number') return null
    const current = editor.state.doc.nodeAt(pos)
    return current?.type.name === 'todoLink' && current.attrs.todoId == null ? pos : null
  }

  const removeSelf = (pos: number | null, opts: { focus: boolean }) => {
    if (pos === null) return
    const chain = opts.focus ? editor.chain().focus() : editor.chain()
    chain
      .command(({ tr }) => {
        // doc は block+ なので唯一のブロックなら段落に置き換える
        if (tr.doc.childCount === 1) {
          tr.replaceWith(pos, pos + 1, editor.schema.nodes.paragraph.create())
          tr.setSelection(Selection.near(tr.doc.resolve(pos + 1)))
        } else {
          tr.delete(pos, pos + 1)
          tr.setSelection(Selection.near(tr.doc.resolve(Math.min(pos, tr.doc.content.size)), -1))
        }
        return true
      })
      .run()
  }

  const linkTodo = (picked: TodoResponse) => {
    const pos = emptyPos()
    if (pos === null) return
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, todoId: picked.id, title: picked.title })
        const after = pos + 1
        if (!tr.doc.resolve(after).nodeAfter?.isTextblock) {
          tr.insert(after, editor.schema.nodes.paragraph.create())
        }
        tr.setSelection(Selection.near(tr.doc.resolve(after + 1)))
        return true
      })
      .run()
  }

  if (todoId === null) {
    if (!canEdit) return <NodeViewWrapper />
    return (
      <NodeViewWrapper className="my-0.5">
        <div
          contentEditable={false}
          className="flex items-center gap-1 rounded-md border border-dashed border-emerald-500/40 bg-emerald-500/8 px-1.5 py-1"
        >
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="flex min-w-0 flex-1 items-center gap-2 p-0! text-left text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-4.5 shrink-0" />
            Todo を追加
          </button>
          <IconButton label="取り消す" onClick={() => removeSelf(emptyPos(), { focus: true })}>
            <X className="size-3.5" />
          </IconButton>
        </div>
        {dialogOpen && (
          <TodoInsertDialog
            onCreate={async (title, project) => {
              linkTodo(await createLinkedTodo(title, project))
            }}
            onPick={linkTodo}
            onClose={() => {
              setDialogOpen(false)
              removeSelf(emptyPos(), { focus: true })
            }}
          />
        )}
      </NodeViewWrapper>
    )
  }

  return (
    <NodeViewWrapper className="my-0.5" data-drag-handle="">
      <div
        contentEditable={false}
        className={cn(
          'group/todo flex items-start gap-2 rounded-md bg-emerald-500/8 px-1.5 py-1 transition-colors hover:bg-emerald-500/15',
          selected && 'outline-2 outline-(--tt-brand-color-500)',
        )}
      >
        {entry === undefined && <LoadingRow title={snapshotTitle} />}
        {entry === 'missing' && (
          <UnavailableRow
            title={snapshotTitle}
            message="削除された Todo"
            action={
              canEdit && (
                <IconButton label="メモから外す" onClick={() => removeSelf(getPos() ?? null, { focus: false })}>
                  <X className="size-3.5" />
                </IconButton>
              )
            }
          />
        )}
        {entry === 'error' && (
          <UnavailableRow
            title={snapshotTitle}
            message="読み込みに失敗しました"
            action={
              <IconButton label="再読み込み" onClick={() => requestLinkedTodo(todoId)}>
                <RotateCw className="size-3.5" />
              </IconButton>
            }
          />
        )}
        {todo && <TodoRow todo={todo} />}
      </div>
    </NodeViewWrapper>
  )
}

function TodoRow({ todo }: { todo: TodoResponse }) {
  const [pendingStatus, setPendingStatus] = useState<TodoStatus | null>(null)
  const status = pendingStatus ?? todo.status
  const isDone = status === 'done'
  const isDismissed = status === 'dismissed'

  const handleToggle = async () => {
    if (pendingStatus) return
    setPendingStatus(isDone ? 'pending' : 'done')
    try {
      await toggleLinkedTodo(todo)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Todo の更新に失敗しました'))
    } finally {
      setPendingStatus(null)
    }
  }

  const deadline = todo.deadline ? parseUtcDate(todo.deadline) : null
  const isOverdue = !isDone && !isDismissed && deadline !== null && deadline.getTime() < Date.now()
  const isDueToday = !isDone && !isDismissed && deadline !== null && !isOverdue && isToday(deadline)

  return (
    <>
      <button
        type="button"
        role="checkbox"
        aria-checked={isDone}
        aria-label={isDone ? '未完了に戻す' : '完了にする'}
        className="mt-0.5 shrink-0 p-0! text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => void handleToggle()}
      >
        <StatusIcon status={status} />
      </button>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span
          className={cn(
            'wrap-break-word min-w-0 transition-colors',
            (isDone || isDismissed) && 'text-muted-foreground line-through',
          )}
        >
          {todo.title}
        </span>
        {status === 'in_progress' && (
          <span className={cn(chipClass, 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400')}>
            進行中
          </span>
        )}
        {isDismissed && <span className={cn(chipClass, 'text-muted-foreground')}>見送り</span>}
        {deadline && !isDone && (
          <span
            className={cn(
              chipClass,
              isOverdue
                ? 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
                : isDueToday
                  ? 'border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400'
                  : 'text-muted-foreground',
            )}
            title="締切"
          >
            <Calendar className="size-3" />
            {formatDateTime(todo.deadline ?? '')}
          </span>
        )}
        {status === 'scheduled' && todo.scheduled_start && (
          <span className={cn(chipClass, 'text-muted-foreground')} title="予定">
            <Clock className="size-3" />
            {formatDateTime(todo.scheduled_start)}
          </span>
        )}
        {todo.project && !isDone && (
          <span className={cn(chipClass, 'max-w-40 truncate text-muted-foreground')}>
            <Folder className="size-3 shrink-0" />
            <span className="truncate">{todo.project}</span>
          </span>
        )}
      </div>

      <Link
        to={`${PATHS.TODO.HOME}?todo=${todo.id}`}
        aria-label="Todo を開く"
        title={`Todo #${todo.id} を開く`}
        className="mt-0.5 flex shrink-0 items-center gap-0.5 rounded px-1 text-[11px] text-muted-foreground transition-opacity hover:bg-accent hover:text-foreground sm:opacity-0 sm:group-hover/todo:opacity-100"
      >
        #{todo.id}
        <ArrowUpRight className="size-3.5" />
      </Link>
    </>
  )
}

function StatusIcon({ status }: { status: TodoStatus }) {
  if (status === 'done') return <CircleCheck className="size-4.5 text-emerald-500" />
  if (status === 'dismissed') return <CircleMinus className="size-4.5" />
  if (status === 'in_progress') return <CircleDot className="size-4.5 text-blue-500" />
  return <Circle className="size-4.5" />
}

function LoadingRow({ title }: { title: string }) {
  return (
    <>
      <Loader2 className="mt-0.5 size-4.5 shrink-0 animate-spin text-muted-foreground" />
      <span className="min-w-0 flex-1 text-muted-foreground">{title || 'Todo を読み込み中…'}</span>
    </>
  )
}

function UnavailableRow({ title, message, action }: { title: string; message: string; action: ReactNode }) {
  return (
    <>
      <CircleMinus className="mt-0.5 size-4.5 shrink-0 text-muted-foreground/60" />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 text-muted-foreground">
        {title && <span className="line-through">{title}</span>}
        <span className="text-xs">({message})</span>
      </div>
      {action}
    </>
  )
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="mt-0.5 shrink-0 rounded p-0.5! text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  )
}
